import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BadgeCheckIcon, LayoutGridIcon, ListIcon, MapIcon, SearchXIcon } from 'lucide-react';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { HeroB } from './HeroB';
import { RefinePanel } from './RefinePanel';
import { UniversityGridCard } from './UniversityGridCard';
import { CompareBarB } from './CompareBarB';
import { SaveButton } from '../SaveButton';
import { UniversityMark } from '../UniversityMark';
import { UniversityMap } from '../UniversityMap';
import { UniversityCompareModal } from '../UniversityCompareModal';
import { ProgrammeResults } from '../sections/ProgrammeResults';
import { ScholarshipResults } from '../sections/ScholarshipResults';
import { FeedSection } from '../sections/FeedSection';
import { SavedSection } from '../sections/SavedSection';
import { ApplicationWorkspace } from '../ApplicationWorkspace';
import { useDiscover, type DiscoverSection, type ViewMode } from '../../../contexts/DiscoverContext';
import { nextDeadline } from '../../../utils/discover';
import { programmes as allProgrammes } from '../../../data/admissions';
import { cn } from '../../../utils/cn';

/**
 * DESIGN B — the discovery workspace composition.
 *
 * Personalised, dense and filter-led. It assumes you already know roughly what
 * you are after and want to narrow it quickly, so refinement is permanently on
 * screen and results are laid out to be scanned rather than read.
 *
 * It renders the shared Discover state. It owns no data of its own.
 */
const SECTIONS: DiscoverSection[] = ['Universities', 'Programmes', 'Scholarships', 'Applications', 'Feed', 'Saved', 'Compare'];
const VIEWS: {
  mode: ViewMode;
  icon: React.ComponentType<{
    className?: string;
  }>;
}[] = [{
  mode: 'Grid',
  icon: LayoutGridIcon
}, {
  mode: 'List',
  icon: ListIcon
}, {
  mode: 'Map',
  icon: MapIcon
}];
const SORTS = ['Recommended', 'Most popular', 'Recently added'] as const;
export function DesignB() {
  const {
    section,
    setSection,
    universities,
    sort,
    setSort,
    view,
    setView,
    clearFilters,
    activeChips,
    savedCount,
    compared,
    compareOpen,
    setCompareOpen,
    isSaved,
    toggleSave,
    isCompared,
    toggleCompare,
    compareFull
  } = useDiscover();
  const showRail = section === 'Universities' || section === 'Programmes' || section === 'Scholarships';
  return <div className={cn('space-y-6', compared.length > 0 && 'pb-28')} data-id="element-1350" data-mp="4o4qmjklyfyr">
      <HeroB data-id="element-1351" data-mp="4qhaj29xf0eg" />

      {/* Discovery navigation --------------------------------------------- */}
      <nav aria-label="Discover sections" className="border-b border-line" data-id="element-1352" data-mp="9pkfkmpwp6mf">
        <ul className="cz-tabstrip cz-tabstrip--flush flex items-center gap-1" data-id="element-1353" data-mp="eo5duglllg88">
          {SECTIONS.map(item => {
          const active = section === item;
          const count = item === 'Saved' ? savedCount : item === 'Compare' ? compared.length : undefined;
          return <li key={item} className="shrink-0" data-id="element-1354" data-mp="3aiyv9mfmr5fj">
                <button type="button" onClick={() => setSection(item)} aria-current={active ? 'page' : undefined} className={cn('relative min-h-touch-lg px-4 text-body-sm transition-colors duration-150 ease-out', active ? 'font-semibold text-brand-800 after:absolute after:inset-x-3 after:bottom-0 after:h-[3px] after:rounded-full after:bg-brand-700' : 'font-medium text-ink-muted hover:text-ink')} data-id="element-1355" data-mp="35k5ixxc2xkgy">
                  {item}
                  {count ? ` (${count})` : ''}
                </button>
              </li>;
        })}
        </ul>
      </nav>

      <div className={cn('grid gap-6', showRail && 'xl:grid-cols-[minmax(0,1fr)_20rem]')} data-id="element-1356" data-mp="g1n93o5pm4pd">
        <div className="min-w-0 space-y-5" data-id="element-1357" data-mp="34w3aoet4ou1t">
          {section === 'Universities' && <>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-3" data-id="element-1358" data-mp="38yhsqwoz3ps1">
                <h2 className="text-h4 font-semibold tracking-tight text-ink" data-id="element-1359" data-mp="2uyfb9bkkuydb">
                  Universities for you
                </h2>
                <div className="cz-tabstrip flex items-center gap-1.5" role="group" aria-label="Order results" data-id="element-1360" data-mp="2zwgsrs2cupmo">
                  {SORTS.map(mode => <button key={mode} type="button" onClick={() => setSort(mode)} aria-pressed={sort === mode} className={cn('min-h-touch shrink-0 rounded-full px-3.5 text-body-sm font-medium transition-colors duration-150 ease-out', sort === mode ? 'bg-brand-50 text-brand-800' : 'text-ink-muted hover:bg-canvas hover:text-ink')} data-id="element-1361" data-mp="2pzc85wo5r27z">
                      {mode}
                    </button>)}
                </div>

                <div className="ml-auto flex items-center gap-1 rounded-lg border border-line bg-surface p-1" role="group" aria-label="Result layout" data-id="element-1362" data-mp="1jntxibid21yv">
                  {VIEWS.map(({
                mode,
                icon: Icon
              }) => <button key={mode} type="button" onClick={() => setView(mode)} aria-pressed={view === mode} className={cn('inline-flex min-h-touch items-center gap-1.5 rounded-md px-3 text-body-sm font-medium transition-colors duration-150 ease-out', view === mode ? 'bg-brand-700 text-white' : 'text-ink-muted hover:bg-canvas hover:text-ink')} data-id="element-1363" data-mp="26k3twujhfnv7">
                      <Icon aria-hidden className="h-4 w-4" data-id="element-1364" data-mp="2sby9tv41snw5" />
                      {mode}
                    </button>)}
                </div>
              </div>

              {activeChips.length > 0 && <p className="text-body-sm text-ink-muted" data-id="element-1365" data-mp="372nm70t8w91l">
                  {universities.length} of 6 institutions match{' '}
                  {activeChips.map(chip => chip.label).join(', ')}.
                </p>}

              {universities.length === 0 ? <div className="rounded-card border border-line bg-surface" data-id="element-1366" data-mp="3g5gen7e5yqt0">
                  <EmptyState icon={<SearchXIcon className="h-5 w-5" data-id="element-1368" data-mp="3q1jfm9xa4jed" />} title="No institutions match what you asked for" description="Nothing has been hidden — no published institution fits every filter at once. Remove one from the refine panel and the list will fill again." action={<Button variant="secondary" onClick={clearFilters} data-id="element-1369" data-mp="1o5atcvbs24lq">
                        Clear all filters
                      </Button>} data-id="element-1367" data-mp="3l3hu7icds7zq" />
                </div> : view === 'Map' ? <UniversityMap universities={universities} onSelect={id => toggleCompare(id)} data-id="element-1370" data-mp="1ec3gkpepjmbm" /> : view === 'List' ? <ul className="divide-y divide-line overflow-hidden rounded-card border border-line bg-surface" data-id="element-1371" data-mp="272bzijh2iar9">
                  {universities.map(university => {
              const owned = allProgrammes.filter(programme => programme.universityId === university.id);
              const soonest = nextDeadline(owned);
              return <li key={university.id} className="flex flex-wrap items-center gap-4 px-4 py-3.5 transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-1372" data-mp="lnediku3d6x8">
                        <UniversityMark university={university} size="sm" data-id="element-1373" data-mp="1befsxc7n1m23" />
                        <div className="min-w-0 flex-1" data-id="element-1374" data-mp="xe4zq2iuvvdr">
                          <p className="flex items-center gap-1.5 text-body font-semibold text-ink" data-id="element-1375" data-mp="xfpu9s567ebm">
                            <Link to={`/discover/universities/${university.id}`} className="truncate hover:underline" data-id="element-1376" data-mp="11g8kxivbuec8">
                              {university.name}
                            </Link>
                            {university.provenance === 'verified' && <BadgeCheckIcon aria-label="Published by the institution" className="h-4 w-4 shrink-0 text-brand-700" data-id="element-1377" data-mp="1kdt203ruo8ma" />}
                          </p>
                          <p className="cz-num truncate text-body-sm text-ink-muted" data-id="element-1378" data-mp="1fmy4l4e4sih">
                            {university.city}, {university.country} · {owned.length} programmes ·{' '}
                            {university.scholarshipsAvailable} scholarships
                          </p>
                        </div>
                        {soonest && <Badge plain className="shrink-0" data-id="element-1379" data-mp="68pvl4cagm3y">
                            {soonest.deadlineLabel}
                          </Badge>}
                        <SaveButton saved={isSaved('university', university.id)} onToggle={() => toggleSave('university', university.id)} subject={university.name} data-id="element-1380" data-mp="39on6jfi9xy3c" />
                        <Button size="sm" variant={isCompared(university.id) ? 'secondary' : 'ghost'} onClick={() => toggleCompare(university.id)} disabled={!isCompared(university.id) && compareFull} data-id="element-1381" data-mp="13tvms343tfvf">
                          {isCompared(university.id) ? 'In comparison' : 'Compare'}
                        </Button>
                        <Link to={`/discover/universities/${university.id}`} data-id="element-1382" data-mp="33b36c4vgevl0">
                          <Button size="sm" variant="primary" icon={<ArrowRightIcon className="h-4 w-4" data-id="element-1384" data-mp="2tcnchu51l87t" />} data-id="element-1383" data-mp="2yd1o1m5ur0al">
                            Explore
                          </Button>
                        </Link>
                      </li>;
            })}
                </ul> : <ul className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-3" data-id="element-1385" data-mp="2fh361y6i2iha">
                  {universities.map(university => <li key={university.id} data-id="element-1386" data-mp="dnvglvx6akho">
                      <UniversityGridCard university={university} data-id="element-1387" data-mp="do57rmtyexoy" />
                    </li>)}
                </ul>}

              <p className="text-caption text-ink-subtle" data-id="element-1388" data-mp="q5y4nkwpcg8h">
                Marks beside each institution are Campuzon identifiers, not official crests. Campus
                photography is representative of the setting and is not supplied by the institution.
              </p>
            </>}

          {section === 'Programmes' && <>
              <h2 className="text-h4 font-semibold tracking-tight text-ink" data-id="element-1389" data-mp="2a3hhucx20y1">Programmes for you</h2>
              <ProgrammeResults density="compact" data-id="element-1390" data-mp="77lt5sibiu9q" />
            </>}

          {section === 'Scholarships' && <>
              <h2 className="text-h4 font-semibold tracking-tight text-ink" data-id="element-1391" data-mp="h59s6umyinxp">
                Funding you could apply for
              </h2>
              <ScholarshipResults density="compact" data-id="element-1392" data-mp="1nktxhsnkgm6p" />
            </>}

          {section === 'Applications' && <ApplicationWorkspace data-id="element-1393" data-mp="1sivco2hvg1cw" />}

          {section === 'Feed' && <FeedSection data-id="element-1394" data-mp="3on1r8kz9uul0" />}

          {section === 'Saved' && <SavedSection data-id="element-1395" data-mp="3opxqccmuvpb1" />}

          {section === 'Compare' && <>
              <h2 className="text-h4 font-semibold tracking-tight text-ink" data-id="element-1396" data-mp="3jo8fawzmvbmy">Side by side</h2>
              {compared.length === 0 ? <div className="rounded-card border border-line bg-surface" data-id="element-1397" data-mp="3tmxuuj424k01">
                  <EmptyState icon={<SearchXIcon className="h-5 w-5" data-id="element-1399" data-mp="35wts0dyp0oa3" />} title="Nothing selected to compare" description="Add up to three institutions from the universities grid. They stay selected while you keep browsing." action={<Button variant="primary" onClick={() => setSection('Universities')} data-id="element-1400" data-mp="39yykh9denev5">
                        Browse universities
                      </Button>} data-id="element-1398" data-mp="3tktgf78j82li" />
                </div> : <>
                  <Button variant="primary" onClick={() => setCompareOpen(true)} data-id="element-1401" data-mp="2i7inqmi4wbr7">
                    Open the comparison
                  </Button>
                  <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-id="element-1402" data-mp="2cv2fm7qyuruj">
                    {compared.map(university => <li key={university.id} data-id="element-1403" data-mp="1y6z7ostd5633">
                        <UniversityGridCard university={university} data-id="element-1404" data-mp="234qytkm6oo68" />
                      </li>)}
                  </ul>
                </>}
            </>}
        </div>

        {showRail && <div className="min-w-0 xl:sticky xl:top-24 xl:self-start" data-id="element-1405" data-mp="2vtoerh2xa8vt">
            <RefinePanel data-id="element-1406" data-mp="2gwe8cm54zt6y" />
          </div>}
      </div>

      {section !== 'Compare' && <CompareBarB data-id="element-1407" data-mp="2lv8othfmt5nw" />}

      <UniversityCompareModal open={compareOpen} onClose={() => setCompareOpen(false)} selected={compared} data-id="element-1408" data-mp="2lupgmsfs9vn2" />
    </div>;
}